// The human approval gate: image requests awaiting review. The exact frozen
// prompt is shown before an owner approves. Approval is required before any
// generation can occur (enforced server-side).

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api.js';
import type { ImageRequest } from '../lib/types.js';

export function PromptReviewPage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const queue = useQuery({
    queryKey: ['image-requests', 'submitted_for_review'],
    queryFn: () => api.get<ImageRequest[]>('/image-requests?status=submitted_for_review'),
  });

  const review = useMutation({
    mutationFn: (vars: { id: string; decision: 'approved' | 'rejected' }) =>
      api.post<ImageRequest>(`/image-requests/${vars.id}/review`, { decision: vars.decision }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['image-requests'] }),
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Review failed'),
  });

  return (
    <section>
      <h1>Prompt Review</h1>
      <p className="muted">
        Approve the exact prompt below before any paid image generation. Approval is mandatory.
      </p>
      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}
      {queue.isLoading && <p>Loading…</p>}
      {queue.data && queue.data.data.length === 0 && (
        <p className="muted">Nothing awaiting review.</p>
      )}
      {queue.data?.data.map((req) => (
        <div key={req.id} className="review-panel">
          <p>
            <strong>Concept:</strong> <Link to={`/concepts/${req.conceptId}`}>{req.conceptId}</Link>{' '}
            · {req.aspectRatio} / {req.imageModelTier}
          </p>
          <blockquote className="frozen-prompt">{req.promptSnapshot}</blockquote>
          <p className="muted">hash: {req.promptHash.slice(0, 16)}…</p>
          <div className="button-row">
            <button
              type="button"
              onClick={() => review.mutate({ id: req.id, decision: 'approved' })}
              disabled={review.isPending}
            >
              Approve
            </button>
            <button
              type="button"
              className="button--danger"
              onClick={() => review.mutate({ id: req.id, decision: 'rejected' })}
              disabled={review.isPending}
            >
              Reject
            </button>
          </div>
        </div>
      ))}
    </section>
  );
}
